import { useId, useState } from 'react';
import { Button } from '@/ui/Button';
import { Dialog } from '@/ui/Dialog';
import { BuscadorDePersonas, type PersonaDelDirectorio } from '@/components/BuscadorDePersonas';
import { cn } from '@/lib/cn';
import { Icono } from './piezas';
import {
  usePublicarReconocimiento, type Valor,
} from './api';

/**
 * Compositor.tsx — «Dar reconocimiento», los tres pasos.
 *
 * ## El defecto que esta pantalla existía para tener
 *
 * El buscador de personas del vanilla construía su manejador interpolando el
 * nombre **dentro de una cadena JavaScript dentro de un atributo HTML**:
 *
 *     onclick="document.getElementById('kudo-search').value='${u.full_name}';"
 *
 * Con `O'Brien Pérez` el atributo que salía era
 * `...value='O'Brien Pérez';` — el apóstrofo cierra la cadena. Comprobado en el
 * navegador el 2026-09-10: **la fila se veía, se podía pulsar y no pasaba
 * nada**; el id del destinatario se quedaba vacío y el buscador conservaba lo
 * escrito. A quien tuviera un apóstrofo en el nombre no se le podía dar un
 * reconocimiento, y no había ningún error que lo dijera.
 *
 * Aquí el nombre no es marcado: es el argumento de un `onClick`. La clase
 * entera de defecto desaparece, no se corrige caso a caso.
 *
 * ## El tope del mensaje
 *
 * Mil caracteres, que es lo que acepta `KudoCreate` en `content.py`. El contador
 * está porque el borde del textarea no avisa de nada, y avisa **antes** de
 * llegar: a partir de 900 cambia de color.
 */

const TOPE_DEL_MENSAJE = 1000;

type Destino = 'persona' | 'empresa';

export function Compositor({
  abierto, alCerrar, valores,
}: {
  abierto: boolean;
  alCerrar: () => void;
  valores: Valor[];
}) {
  const [destino, setDestino] = useState<Destino>('persona');
  const [persona, setPersona] = useState<PersonaDelDirectorio | null>(null);
  const [valor, setValor] = useState('');
  const [mensaje, setMensaje] = useState('');

  const publicar = usePublicarReconocimiento();
  const idBusqueda = useId();
  const idMensaje = useId();

  function limpiar() {
    setDestino('persona');
    setPersona(null);
    setValor('');
    setMensaje('');
    publicar.reset();
  }

  function cerrar() {
    limpiar();
    alCerrar();
  }

  const faltaPersona = destino === 'persona' && !persona;
  const puedeEnviar = !faltaPersona && !!valor && !publicar.isPending;

  function enviar() {
    if (!puedeEnviar) return;
    publicar.mutate(
      {
        receiver_id: destino === 'empresa' ? null : persona!.id,
        to_company: destino === 'empresa',
        badge: valor,
        message: mensaje.trim() || null,
      },
      {
        onSuccess: () => {
          Utils.confetti?.({ count: 80 });
          Utils.toast('¡Reconocimiento publicado!', 'success');
          cerrar();
        },
        onError: (err: unknown) => {
          Utils.toast((err as Error)?.message || 'No se pudo publicar', 'error');
        },
      },
    );
  }

  return (
    <Dialog
      abierto={abierto}
      onCambio={(v) => { if (!v) cerrar(); }}
      titulo="Dar reconocimiento"
      ancho="max-w-xl"
      pie={
        <>
          <Button variant="ghost" onClick={cerrar}>Cancelar</Button>
          <Button onClick={enviar} disabled={!puedeEnviar}>
            <i className="bi bi-send-fill" aria-hidden="true" />
            {publicar.isPending ? 'Publicando…' : 'Enviar reconocimiento'}
          </Button>
        </>
      }
    >
      <p className="kudo-guia">
        Un reconocimiento se lee una vez y se recuerda mucho tiempo. Tómate treinta
        segundos: los tres pasos son cortos.
      </p>

      {/* ── 1 · A quién ──────────────────────────────────────────────── */}
      <section className="kudo-paso">
        <h4 className="kudo-paso-cab"><span className="kudo-paso-n">1</span> A quién</h4>
        {/* `radiogroup` y no dos botones sueltos: son opciones excluyentes, y
            así las flechas del teclado se mueven entre ellas. El vanilla usaba
            `aria-pressed`, que describe un interruptor y no una elección. */}
        <div className="kudo-destinos" role="radiogroup" aria-label="A quién se reconoce">
          {([
            ['persona', 'bi-person', 'Una persona'],
            ['empresa', 'bi-building', 'Toda la empresa'],
          ] as const).map(([clave, icono, etiqueta]) => (
            <button
              key={clave}
              type="button"
              role="radio"
              aria-checked={destino === clave}
              className={cn('kudo-target', destino === clave && 'activo')}
              onClick={() => setDestino(clave)}
            >
              <i className={cn('bi', icono)} aria-hidden="true" />
              <span>{etiqueta}</span>
            </button>
          ))}
        </div>

        {destino === 'persona' && (
          <div className="mt-3">
            <BuscadorDePersonas
              id={idBusqueda}
              etiqueta="Buscar a la persona por su nombre"
              marcador="Escribe el nombre del colaborador…"
              elegida={persona}
              alElegir={setPersona}
              alSoltar={() => setPersona(null)}
            />
          </div>
        )}
      </section>

      {/* ── 2 · Por qué ──────────────────────────────────────────────── */}
      <section className="kudo-paso">
        <h4 className="kudo-paso-cab"><span className="kudo-paso-n">2</span> Por qué</h4>
        <div className="kudo-valores" role="radiogroup" aria-label="Valor que se reconoce">
          {valores.map((v) => (
            <button
              key={v.key}
              type="button"
              role="radio"
              aria-checked={valor === v.key}
              className={cn('badge-option', valor === v.key && 'activo')}
              onClick={() => setValor(v.key)}
            >
              <span className="badge-option-icono">
                <Icono nombre={v.icon} respaldo="bi-star" />
              </span>
              <span className="badge-option-nombre">{v.name}</span>
              {v.description && <span className="badge-option-desc">{v.description}</span>}
            </button>
          ))}
        </div>
        {!valores.length && (
          <p className="text-3 text-muted">
            No hay valores definidos todavía. Créalos desde «Valores».
          </p>
        )}
      </section>

      {/* ── 3 · Qué hizo ─────────────────────────────────────────────── */}
      <section className="kudo-paso">
        <h4 className="kudo-paso-cab">
          <span className="kudo-paso-n">3</span> Qué hizo exactamente
        </h4>
        {/* Un `<label>` de verdad, con el texto en `sr-only` porque el
            encabezado del paso ya lo dice a la vista. No se usa el `<Textarea>`
            de `ui/Field` porque exige ir dentro de un `<Field>`, y `Field`
            pintaría una segunda etiqueta encima del mismo control. */}
        <label>
          <span className="sr-only">Qué hizo exactamente</span>
          <textarea
            id={idMensaje}
            rows={4}
            maxLength={TOPE_DEL_MENSAJE}
            value={mensaje}
            onChange={(e) => setMensaje(e.target.value)}
            aria-describedby={`${idMensaje}-ayuda`}
            className="w-full rounded-sm border border-field-border bg-surface px-3 py-2
                       text-3 text-ink focus-visible:focus-anillo"
            placeholder="Se quedó hasta tarde resolviendo la falla del turno de la noche, avisó al equipo entrante y dejó el reporte escrito."
          />
        </label>
        <div className="kudo-msg-pie">
          <span id={`${idMensaje}-ayuda`}>
            Un hecho concreto vale más que un «buen trabajo».
          </span>
          {/* `aria-live="polite"` para que quien no ve la pantalla se entere de
              que se está acercando al tope, y no solo al chocar con él. */}
          <span
            className={cn('kudo-cuenta', mensaje.length > 900 && 'cerca')}
            aria-live="polite"
          >
            {mensaje.length} / {TOPE_DEL_MENSAJE}
          </span>
        </div>
      </section>

      <p className="kudo-alcance">
        <i className="bi bi-megaphone" aria-hidden="true" /> Se publica en el muro: lo verá
        toda la compañía.
      </p>
    </Dialog>
  );
}
